import React from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { prompt } from '@dashboard/lib/prompt';
import { fetcher } from '@dashboard/services/request';
import { useSwitchStore } from '@dashboard/hooks/useSwitchStore';
import { toast } from '@dashboard/context/ToastContext';
import SettingsDivider from '@dashboard/components/SettingsDivider';
import SettingsDescription from '@dashboard/components/SettingsDescription';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import Button from '@dashboard/components/Button';

const DeleteStoreSection = () => {
  const { user, seller, isOwner, token, refreshAvailableStores, updateAuthContext } = useAuth();
  const { switchStore } = useSwitchStore();
  const history = useHistory();

  if (!isOwner) {
    return null;
  }

  const storeName = seller.displayName || seller.name;

  async function handleDeleteStore() {
    try {
      await prompt('Delete store', {
        description: (
          <>
            This will permanently delete your store "<strong>{storeName}</strong>" and all its data.
            Type <strong>{storeName}</strong> to confirm.
          </>
        ),
        placeholder: `Type ${storeName}`,
        confirmText: 'Delete store permanently',
        cancelText: 'Cancel',
        validate: value => {
          if (value !== storeName) {
            return 'The name you entered does not match. Please try again.';
          }
        },
      });
    } catch {
      return;
    }

    try {
      await fetcher(`/sellers/${seller._id}`, token, { method: 'DELETE' });

      toast({
        title: 'Store deleted',
        description: `${storeName} and all its data have been permanently removed.`,
      });

      const updatedStores = await refreshAvailableStores();

      // null means the refresh failed, not that no stores remain. Clearing the
      // seller here would leave an owner who still has stores with no store
      // switcher; the API picks the next active seller on the next request.
      if (!updatedStores) {
        history.replace('/');
        return;
      }

      const remainingStores = updatedStores.filter(s => s._id !== seller._id && !s.disabled);
      if (remainingStores.length > 0) {
        await switchStore(remainingStores[0]._id);
        history.replace('/');
      } else {
        updateAuthContext({
          user,
          seller: {},
          availableStores: [],
        });

        history.replace('/account/profile');
      }
    } catch (error) {
      toast({
        title: 'Unable to delete store',
        description: error?.message || 'Please contact support.',
        type: 'error',
      });
    }
  }

  return (
    <>
      <SettingsDivider />

      <SettingsHeader id="danger-zone">Delete store</SettingsHeader>
      <SettingsDescription className="mb-4">
        Deleting this store will permanently remove all pages, products, customers, payments,
        subscriptions, analytics, and data. This action cannot be undone.
      </SettingsDescription>

      <Button handleClick={handleDeleteStore} appearance="light">
        Delete this store permanently
      </Button>
    </>
  );
};

export default DeleteStoreSection;
